import { ArrowUpRight, Bot, Layers, RadioTower } from 'lucide-react';
import { PageHeader } from '../components/AppShell';
import { PageCaption } from '../components/PageCaption';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Empty } from '../components/ui/empty';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import { Switch } from '../components/ui/switch';
import {
  type StoryCluster,
  useStoryClusters,
} from '../lib/api/newsDeskClusters';
import { fmtDateTime, fmtRelative } from '../lib/date';
import { usePersistedState } from '../lib/usePersistedState';

export default function Stories(): JSX.Element {
  // Persisted across sessions so editors don't have to re-pick the same window
  // every visit. Keyed under a single namespace so future Stories prefs live together.
  const [windowHours, setWindowHours] = usePersistedState<number>('stories:windowHours', 72);
  const [minSources, setMinSources] = usePersistedState<number>('stories:minSources', 3);
  const [summariesOn, setSummariesOn] = usePersistedState<boolean>('stories:summariesOn', false);

  const q = useStoryClusters({ windowHours, minSources, summaries: summariesOn });
  const cfg = q.data?.config;
  const clusters = q.data?.clusters ?? [];

  return (
    <>
      <Topbar title="Stories" subtitle="Developing stories across watched feeds" />
      <PageHeader
        title="Stories"
        eyebrow="Newsroom · Evolution Tracker"
        subtitle={
          q.isLoading
            ? 'Loading…'
            : `${clusters.length} ${clusters.length === 1 ? 'story' : 'stories'} with ≥${minSources} source${minSources === 1 ? '' : 's'} in the last ${windowHours}h`
        }
        actions={
          <div className="flex items-center gap-2">
            <Select value={String(windowHours)} onValueChange={(v) => setWindowHours(Number(v))}>
              <SelectTrigger className="h-8 w-[110px] text-[12.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24">Last 24h</SelectItem>
                <SelectItem value="48">Last 48h</SelectItem>
                <SelectItem value="72">Last 72h</SelectItem>
                <SelectItem value="168">Last 7d</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(minSources)} onValueChange={(v) => setMinSources(Number(v))}>
              <SelectTrigger className="h-8 w-[120px] text-[12.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">≥2 sources</SelectItem>
                <SelectItem value="3">≥3 sources</SelectItem>
                <SelectItem value="4">≥4 sources</SelectItem>
                <SelectItem value="5">≥5 sources</SelectItem>
              </SelectContent>
            </Select>
            {cfg?.llmConfigured ? (
              <label
                title="Generate a one-line summary per story via Groq"
                className="ml-1 flex items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1 text-[11.5px] font-medium text-ink"
              >
                <Bot className="h-3.5 w-3.5 text-brand" />
                <span>AI summaries</span>
                <Switch
                  checked={summariesOn}
                  onCheckedChange={setSummariesOn}
                  aria-label="Toggle AI summaries"
                />
              </label>
            ) : null}
          </div>
        }
      />
      <PageCaption id="stories" icon={Layers}>
        <strong className="text-ink">Stories</strong> — the same external feeds
        as News desk, but AI-clustered: headlines describing the same
        developing story across multiple outlets are grouped into one row.
        Use it to track multi-source stories without re-reading every
        individual headline. Toggle <strong>AI summaries</strong> for a
        one-line synopsis per cluster.
      </PageCaption>

      <div className="space-y-4 px-6 pb-10">
        {q.isLoading ? (
          <Card className="p-6">
            <Spinner label="Clustering headlines…" />
          </Card>
        ) : clusters.length === 0 ? (
          <Empty
            title="No converging stories"
            description={
              minSources > 2
                ? 'Lower the source threshold to see more clusters, or wait for upstream feeds to refresh.'
                : 'The cache is warming or no stories have echoed across multiple feeds in this window.'
            }
          />
        ) : (
          <>
            {clusters.map((c, i) => (
              <ClusterCard key={c.id} cluster={c} rank={i + 1} />
            ))}
          </>
        )}
      </div>
    </>
  );
}

function ClusterCard({ cluster, rank }: { cluster: StoryCluster; rank: number }): JSX.Element {
  const sourceCount = cluster.sources.length;
  const itemCount = cluster.items.length;
  const developing = cluster.latestAt && cluster.oldestAt
    ? new Date(cluster.latestAt).getTime() - new Date(cluster.oldestAt).getTime() > 4 * 60 * 60_000
    : false;

  return (
    <Card className="overflow-hidden">
      {/* Header band */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line/70 px-5 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-heading text-[18px] font-bold leading-none tracking-tight text-brand tabular">
            {String(rank).padStart(2, '0')}
          </span>
          <Badge variant="brand" className="font-mono text-[10.5px]">
            <RadioTower className="h-3 w-3" /> {sourceCount} source{sourceCount === 1 ? '' : 's'}
          </Badge>
          <Badge variant="neutral" className="font-mono text-[10.5px]">
            <Layers className="h-3 w-3" /> {itemCount} headline{itemCount === 1 ? '' : 's'}
          </Badge>
          {developing ? (
            <span className="rounded-full bg-warning/10 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-warning">
              Developing
            </span>
          ) : null}
        </div>
        <span
          className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink-subtle"
          title={cluster.latestAt ? fmtDateTime(cluster.latestAt) : ''}
        >
          latest {cluster.latestAt ? fmtRelative(cluster.latestAt) : '—'}
        </span>
      </div>

      {/* Optional LLM summary */}
      {cluster.summary ? (
        <div className="border-b border-line/50 bg-brand-soft/30 px-5 py-3">
          <p className="flex gap-2 text-[13.5px] leading-snug text-ink">
            <Bot className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <span className="italic">{cluster.summary}</span>
          </p>
        </div>
      ) : null}

      {/* Source pill row */}
      <div className="flex flex-wrap gap-1.5 border-b border-line/40 bg-surface-tinted/30 px-5 py-2">
        {cluster.sources.map((s) => (
          <span
            key={s}
            className="rounded-sm bg-surface px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-ink-muted ring-1 ring-line"
          >
            {s}
          </span>
        ))}
      </div>

      {/* Timeline */}
      <ol className="divide-y divide-line/60">
        {cluster.items.map((it) => (
          <li key={it.url} className="flex items-start gap-3 px-5 py-3">
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand/60" />
            <div className="min-w-0 flex-1">
              <a
                href={it.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-baseline gap-2 text-[13.5px] font-medium leading-snug text-ink hover:text-brand"
              >
                <span className="min-w-0 flex-1 line-clamp-2">{it.title}</span>
                <ArrowUpRight className="mt-0.5 h-3 w-3 shrink-0 text-ink-subtle transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-brand" />
              </a>
              <p className="mt-1 flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.1em] text-ink-subtle">
                <span>{it.feedLabel}</span>
                <span aria-hidden className="h-1 w-1 rounded-full bg-line-strong" />
                <span
                  className="normal-case tracking-normal"
                  title={
                    it.publishedAt
                      ? fmtDateTime(it.publishedAt)
                      : `Observed at ${fmtDateTime(it.observedAt)}`
                  }
                >
                  {fmtRelative(it.publishedAt ?? it.observedAt)}
                </span>
              </p>
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}
